import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Clock, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TopBar } from '@/components/TopBar';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { HreflangTags } from '@/components/HreflangTags';
import { useLanguage } from '@/i18n/LanguageContext';

const BlogPage = () => {
  const { t, language } = useLanguage();

  // Format date based on language
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const localeMap = {
      en: 'en-US',
      lv: 'lv-LV',
      lt: 'lt-LT',
      ee: 'et-EE',
    };
    return date.toLocaleDateString(localeMap[language] || 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // New blog articles with real content - sorted newest first
  const newBlogPosts = [
    {
      id: 'new-5',
      slug: 'integrate-connected-prevention-home-insurance',
      title: t('blogArticles.integrate-connected-prevention-home-insurance.title'),
      excerpt: t('blogArticles.integrate-connected-prevention-home-insurance.metaDesc'),
      category: t('blogArticles.insurance'),
      date: '2026-06-12',
      readTime: '10',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=400&fit=crop',
      hasFullArticle: true,
    },
    {
      id: 'new-4',
      slug: 'early-leak-detection-insurance-economics',
      title: t('blogArticles.early-leak-detection-insurance-economics.title'),
      excerpt: t('blogArticles.early-leak-detection-insurance-economics.metaDesc'),
      category: t('blogArticles.insurance'),
      date: '2026-06-11',
      readTime: '9',
      image: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=800&h=400&fit=crop',
      hasFullArticle: true,
    },
    {
      id: 'new-3',
      slug: 'nursing-home-monitoring-systems',
      title: t('blogArticles.nursing-home-monitoring-systems.title'),
      excerpt: t('blogArticles.nursing-home-monitoring-systems.metaDesc'),
      category: t('blogArticles.elderlyCare'),
      date: '2026-03-05',
      readTime: '14',
      image: 'https://images.unsplash.com/photo-1586105251261-72a756497a11?w=800&h=400&fit=crop',
      hasFullArticle: true,
    },
    {
      id: 'new-2',
      slug: 'fall-detection-elderly-home',
      title: t('blogArticles.fall-detection-elderly-home.title'),
      excerpt: t('blogArticles.fall-detection-elderly-home.metaDesc'),
      category: t('blogArticles.elderlyCare'),
      date: '2026-02-10',
      readTime: '10',
      image: 'https://images.unsplash.com/photo-1581579438747-1dc8d17bbce4?w=800&h=400&fit=crop',
      hasFullArticle: true,
    },
    {
      id: 'new-1',
      slug: 'monitor-elderly-parents-remotely',
      title: t('blogArticles.monitor-elderly-parents-remotely.title'),
      excerpt: t('blogArticles.monitor-elderly-parents-remotely.metaDesc'),
      category: t('blogArticles.elderlyCare'),
      date: '2026-01-15',
      readTime: '12',
      image: 'https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?w=800&h=400&fit=crop',
      hasFullArticle: true,
    },
  ];

  // Sample blog posts - using translations (older articles)
  const blogPosts = [
    ...newBlogPosts,
    {
      id: 1,
      slug: 'lorawan-smart-city-infrastructure',
      title: t('blog.article1Title'),
      excerpt: t('blog.article1Excerpt'),
      category: t('blog.smartCities'),
      date: '2025-12-10',
      readTime: '8',
      image: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=800&h=400&fit=crop',
      hasFullArticle: false,
    },
    {
      id: 2,
      slug: 'indoor-air-quality-monitoring-schools',
      title: t('blog.article2Title'),
      excerpt: t('blog.article2Excerpt'),
      category: t('blog.indoorAirQuality'),
      date: '2025-12-05',
      readTime: '10',
      image: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=800&h=400&fit=crop',
      hasFullArticle: false,
    },
    {
      id: 3,
      slug: 'smart-metering-utilities',
      title: t('blog.article3Title'),
      excerpt: t('blog.article3Excerpt'),
      category: t('blog.smartMetering'),
      date: '2025-11-28',
      readTime: '12',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=400&fit=crop',
      hasFullArticle: false,
    },
    {
      id: 4,
      slug: 'asset-tracking-logistics',
      title: t('blog.article4Title'),
      excerpt: t('blog.article4Excerpt'),
      category: t('blog.assetTracking'),
      date: '2025-11-20',
      readTime: '7',
      image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&h=400&fit=crop',
      hasFullArticle: false,
    },
    {
      id: 5,
      slug: 'building-energy-efficiency-iot',
      title: t('blog.article5Title'),
      excerpt: t('blog.article5Excerpt'),
      category: t('blog.energyEfficiency'),
      date: '2025-11-15',
      readTime: '9',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=400&fit=crop',
      hasFullArticle: false,
    },
    {
      id: 6,
      slug: 'tektelic-breeze-co2-sensor-review',
      title: t('blog.article6Title'),
      excerpt: t('blog.article6Excerpt'),
      category: t('blog.productReviews'),
      date: '2025-11-08',
      readTime: '6',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=400&fit=crop',
      hasFullArticle: false,
    },
  ];

  const categories = [
    { key: 'all', label: t('blog.all') },
    { key: 'insurance', label: t('blogArticles.insurance') },
    { key: 'elderlyCare', label: t('blogArticles.elderlyCare') },
    { key: 'smartCities', label: t('blog.smartCities') },
    { key: 'indoorAirQuality', label: t('blog.indoorAirQuality') },
    { key: 'smartMetering', label: t('blog.smartMetering') },
    { key: 'assetTracking', label: t('blog.assetTracking') },
    { key: 'energyEfficiency', label: t('blog.energyEfficiency') },
    { key: 'productReviews', label: t('blog.productReviews') },
  ];

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Blog | IoT Insights & Smart Solutions | 0g Baltics</title>
        <meta name="description" content="Explore articles on LoRaWAN infrastructure, smart cities, indoor air quality monitoring, asset tracking, and IoT best practices from 0g Baltics experts." />
        <meta name="keywords" content="IoT blog, LoRaWAN articles, smart city insights, indoor air quality, asset tracking, smart metering, building automation" />
        <link rel="canonical" href="https://0gbaltics.com/blog" />
        <meta property="og:title" content="Blog | 0g Baltics" />
        <meta property="og:description" content="IoT insights and smart solutions from 0g Baltics experts." />
        <meta property="og:type" content="website" />
      </Helmet>
      <HreflangTags />

      <TopBar />
      <Header />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-slate-900" data-testid="blog-hero">
          <div className="container-0g">
            <div className="max-w-3xl">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                {t('blog.badge')}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                {t('blog.title')}
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed">
                {t('blog.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-8 bg-white border-b border-slate-200 sticky top-16 z-30">
          <div className="container-0g">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map((category, index) => (
                <button
                  key={category.key}
                  className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
                    index === 0
                      ? 'bg-teal-600 text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`category-${category.key}`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-16 md:py-24 bg-slate-50" data-testid="blog-posts">
          <div className="container-0g">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => {
                const ArticleWrapper = post.hasFullArticle ? Link : 'div';
                const wrapperProps = post.hasFullArticle ? { to: `/blog/${post.slug}` } : {};
                
                return (
                  <ArticleWrapper key={post.id} {...wrapperProps}>
                    <article 
                      className={`bg-white rounded-xl overflow-hidden border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all group h-full ${post.hasFullArticle ? 'cursor-pointer' : ''}`}
                      data-testid={`blog-post-${post.slug}`}
                    >
                      {/* Image */}
                      <div className="aspect-video overflow-hidden bg-slate-100">
                        <img 
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>
                      
                      {/* Content */}
                      <div className="p-6">
                        {/* Category & Date */}
                        <div className="flex items-center gap-3 mb-3">
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-teal-600 bg-teal-50 px-2 py-1 rounded">
                            <Tag className="h-3 w-3" />
                            {post.category}
                          </span>
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {post.readTime} {t('blog.minRead')}
                          </span>
                        </div>
                        
                        {/* Title */}
                        <h2 className="text-lg font-semibold text-slate-900 mb-3 line-clamp-2 group-hover:text-teal-600 transition-colors">
                          {post.title}
                        </h2>
                        
                        {/* Excerpt */}
                        <p className="text-sm text-slate-600 mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>
                        
                        {/* Footer */}
                        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(post.date)}
                          </span>
                          <span className="text-sm font-medium text-teal-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                            {t('blog.readMore')}
                            <ArrowRight className="h-4 w-4" />
                          </span>
                        </div>
                      </div>
                    </article>
                  </ArticleWrapper>
                );
              })}
            </div>

            {/* Load More Button */}
            <div className="text-center mt-12">
              <Button 
                variant="outline"
                className="px-8 py-3 text-slate-600 border-slate-300 hover:bg-slate-100"
                data-testid="load-more-posts"
              >
                {t('blog.loadMore')}
              </Button>
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-16 md:py-24 bg-slate-900" data-testid="blog-newsletter">
          <div className="container-0g">
            <div className="max-w-2xl mx-auto text-center">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                {t('blog.newsletterBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                {t('blog.newsletterTitle')}
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                {t('blog.newsletterSubtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input 
                  type="email"
                  placeholder={t('blog.newsletterPlaceholder')}
                  className="flex-grow px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  data-testid="newsletter-email"
                />
                <Button 
                  className="btn-primary px-6 py-3"
                  data-testid="newsletter-subscribe"
                >
                  {t('blog.newsletterButton')}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-4">
                {t('blog.newsletterDisclaimer')}
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default BlogPage;
